/**
 * Organization (tenant) context. Holds the active org, the selectable org list,
 * and `switchOrg`. The active org is persisted so a reload restores it.
 *
 * This is tenancy-generic — it knows nothing about demo mode. Demo builds extend
 * the org list (P2) and the rest of the app reads the active org via `useOrg()`.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { OrgDescriptor, OrgId } from '@wand/shared';
import { setOrgDescriptors } from './derived.js';

/** The default org list. Demo mode (P2) supplies a richer list via the `orgs` prop. */
export const DEFAULT_ORGS: OrgDescriptor[] = [
  { id: 'wand-inc', name: 'Wand, Inc.', short: 'WI', tagline: 'Strategy' },
];

const STORAGE_KEY = 'wand-active-org';

/**
 * `?org=<id>` selects the org for this visit, ahead of whatever is in storage.
 *
 * The active org is otherwise localStorage-only and never appears in the URL, so a link to a
 * particular org cannot exist — which is a problem when a demo is delivered by email and the
 * recipient's browser already remembers a different one. Reading it once at boot makes the
 * link work; it is then persisted like any other choice, so a reload keeps it.
 */
function orgFromUrl(orgs: OrgDescriptor[]): OrgId | undefined {
  try {
    const requested = new URLSearchParams(window.location.search).get('org');
    if (requested && orgs.some((o) => o.id === requested)) return requested;
  } catch {
    /* ignore */
  }
  return undefined;
}

function loadActiveOrg(orgs: OrgDescriptor[]): OrgId {
  const fromUrl = orgFromUrl(orgs);
  if (fromUrl) {
    try {
      localStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      /* ignore */
    }
    return fromUrl;
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && orgs.some((o) => o.id === saved)) return saved;
  } catch {
    /* ignore */
  }
  return orgs[0]!.id;
}

interface OrgContextValue {
  orgId: OrgId;
  org: OrgDescriptor;
  orgs: OrgDescriptor[];
  switchOrg: (id: OrgId) => void;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function OrgProvider({
  orgs = DEFAULT_ORGS,
  children,
}: {
  orgs?: OrgDescriptor[];
  children: ReactNode;
}) {
  // Publish the descriptors to the synchronous registry before any child renders. Several
  // lookups outside React resolve a derived org through its baseline (scenarios, boot payloads,
  // the CEO persona map) and cannot await a fetch. Idempotent, so re-running is harmless.
  useMemo(() => setOrgDescriptors(orgs), [orgs]);

  const [orgId, setOrgId] = useState<OrgId>(() => loadActiveOrg(orgs));

  const switchOrg = useCallback(
    (id: OrgId) => {
      if (!orgs.some((o) => o.id === id)) return;
      setOrgId(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
    },
    [orgs],
  );

  const value = useMemo<OrgContextValue>(() => {
    const org = orgs.find((o) => o.id === orgId) ?? orgs[0]!;
    return { orgId, org, orgs, switchOrg };
  }, [orgId, orgs, switchOrg]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within an OrgProvider');
  return ctx;
}

/** Convenience: just the active org id (the common case for query keys). */
export function useOrgId(): OrgId {
  return useOrg().orgId;
}
